import { useMemo } from "react"

import { request } from "shared/api/request"
import { Logger } from "shared/lib/Logger"
import { generateUUID } from "shared/lib/generateUUID"
import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { AttachmentType } from "shared/types/__generated__/graphql"

import { useDeliveryTimeActiveImportQuery } from "entities/products/graphql/product-information.queries.generated"

export interface ProductDeliveryTimeUploadModel {
  fileLabel?: React.ReactNode
  inputLabel?: React.ReactNode

  file: AttachmentType | null | undefined

  isReadonly?: boolean
  isLoading?: boolean
  isError?: boolean
  onUpload: (files: FilePreview) => Promise<ApiResponse<unknown>>
}

export function useProductDeliveryTimeUploadModel(): ProductDeliveryTimeUploadModel {
  const {
    data,
    refetch,
    loading: isLoadingActiveImport
  } = useDeliveryTimeActiveImportQuery()

  const handleUpload = async (file: FilePreview) => {
    const uploadUrl = `${
      import.meta.env.VITE_API_BASE
    }/product-information/delivery-times?fileName=${file.file.name}`

    return request(uploadUrl, {
      method: "POST",
      body: file.file,
      headers: {
        "Content-Type": file.file.type
      }
    })
      .then(() => {
        return { data: null }
      })
      .catch(err => {
        Logger.error("Product Delivery Time Upload Failed", err)
        return { data: null, errors: [{ message: "Upload failed" }] }
      })
      .finally(() => {
        // There is a delay before the current file name can be retreived.
        // This should be optimized in the backend.
        setTimeout(() => {
          refetch()
        }, 500)
      })
  }

  const file: AttachmentType | null = useMemo(() => {
    if (!data?.deliveryTimeActiveImport) return null
    return {
      ...data?.deliveryTimeActiveImport,
      __typename: "AttachmentType",
      id: generateUUID(),
      name: data?.deliveryTimeActiveImport?.fileName
    }
  }, [data?.deliveryTimeActiveImport])

  return {
    file,
    isLoading: isLoadingActiveImport,
    onUpload: handleUpload
  }
}
